import MissingAttendancesPanel from './MissingAttendancesPanel';
import CollapsibleCard from './CollapsibleCard';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { formatMoney } from '../../utils/money';
import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Spin, message, Tag, List, Typography, Empty, Button, Collapse, Tooltip, Skeleton, Image, Badge } from 'antd';
import {
  TeamOutlined, DollarOutlined, CalendarOutlined, UserAddOutlined,
  WarningOutlined, FileTextOutlined, UserOutlined, UserSwitchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { getDashboardReport, getMonthlyTrend } from '../../api/reports';
import { getDailyNotes } from '../../api/dailyNotes';
import { getDailySchedule, updateSlotTeachers } from '../../api/schedule';
import { getAnnouncements } from '../../api/announcements';
import useAuthStore from '../../store/authStore';
import api from '../../api/axios';
import UserSearchSelect from '../common/UserSearchSelect';
import MissingProjectsPanel from './MissingProjectsPanel';
import AgendaPendingPanel from './AgendaPendingPanel';
import WorkTaskPendingPanel from './WorkTaskPendingPanel';
import RoutinesTodayPanel from './RoutinesTodayPanel';
import ParentRequestsPendingPanel from './ParentRequestsPendingPanel';
import ParentProfileChangePendingPanel from './ParentProfileChangePendingPanel';
import LeaveRequestsPendingPanel from './LeaveRequestsPendingPanel';
import DraggableDashboard from './DraggableDashboard';

const { Text } = Typography;
const PIE_COLORS = ['#1677ff', '#faad14'];

const SectionSpin = () => (
  <div className="flex items-center justify-center py-6"><Spin /></div>
);

const BranchManagerDashboard = () => {
  const { user } = useAuthStore();
  const [data, setData]               = useState(null);
  const [trend, setTrend]             = useState([]);
  const [notes, setNotes]             = useState([]);
  const [lessonNotes, setLessonNotes] = useState([]);
  const [assignState, setAssignState]         = useState({});
  const [announcements, setAnnouncements]     = useState([]);
  const [classroomOccupancy, setClassroomOcc] = useState([]);
  const [absences, setAbsences]               = useState([]);

  const [loadingMain, setLoadingMain]         = useState(true);
  const [loadingTrend, setLoadingTrend]       = useState(true);
  const [loadingNotes, setLoadingNotes]       = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [loadingAbsences, setLoadingAbsences] = useState(true);

  const navigate = useNavigate();
  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    Promise.all([
      getDashboardReport(),
      getAnnouncements({ per_page: 3, is_active: 1 }),
    ]).then(([dashRes, annRes]) => {
      setData(dashRes.data);
      setAnnouncements(annRes.data?.data || []);
    }).catch(() => message.error('Dashboard verileri yüklenemedi.'))
      .finally(() => setLoadingMain(false));

    getMonthlyTrend()
      .then(r => setTrend(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingTrend(false));

    getDailyNotes({ date: today })
      .then(r => setNotes(r.data?.data || r.data || []))
      .catch(() => {})
      .finally(() => setLoadingNotes(false));

    getDailySchedule({ date: today, branch_id: user?.branch_id })
      .then(r => {
        const slots = r.data || [];
        setLessonNotes(slots.filter(s => s.note));
        const map = {};
        slots.forEach(s => {
          const key = s.classroom_id;
          if (!map[key]) map[key] = { name: s.classroom_name, total: 0, capacity_sum: 0, session_count: 0 };
          map[key].total         += s.total || 0;
          map[key].capacity_sum  += s.capacity || 0;
          map[key].session_count += 1;
        });
        setClassroomOcc(Object.values(map).map(c => ({
          ...c,
          rate: c.capacity_sum > 0 ? Math.round((c.total / c.capacity_sum) * 100) : 0,
        })).sort((a, b) => b.rate - a.rate));
      })
      .catch(() => {})
      .finally(() => setLoadingSchedule(false));

    api.get('/reports/today-absences')
      .then(r => setAbsences(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingAbsences(false));
  }, []);


  const toggleAssign = (id) =>
    setAssignState(prev => ({ ...prev, [id]: { open: !prev[id]?.open, selected: [], saving: false } }));

  const handleAssign = async (session) => {
    const st = assignState[session.id];
    if (!st?.selected?.length) return message.warning('Öğretmen seçin.');
    setAssignState(prev => ({ ...prev, [session.id]: { ...prev[session.id], saving: true } }));
    try {
      await updateSlotTeachers({
        date: session.date,
        classroom_id: session.classroom?.id,
        start_time: session.start_time,
        teacher_ids: st.selected,
      });
      message.success('Öğretmen atandı.');
      setData(prev => ({
        ...prev,
        unassigned_sessions: prev.unassigned_sessions.filter(s => s.id !== session.id),
      }));
    } catch {
      message.error('Atama yapılamadı.');
      setAssignState(prev => ({ ...prev, [session.id]: { ...prev[session.id], saving: false } }));
    }
  };

  const trendFormatted = trend.map(t => ({
    ...t,
    monthLabel: dayjs(t.month + '-01').format('MMM YY'),
    revenue: Number(t.revenue),
  }));

  const studentPie    = [
    { name: 'Aktif', value: data?.students?.active   ?? 0 },
    { name: 'Aday',  value: data?.students?.candidate ?? 0 },
  ];
  const attendancePie = [
    { name: 'Geldi',   value: data?.today?.present ?? 0 },
    { name: 'Gelmedi', value: data?.today?.absent  ?? 0 },
  ];

  const widgets = [];

  widgets.push({ id: 'agenda', content: <AgendaPendingPanel /> });
  widgets.push({ id: 'work-tasks', content: <WorkTaskPendingPanel /> });
  widgets.push({ id: 'routines-today', content: <RoutinesTodayPanel /> });
  widgets.push({ id: 'parent-requests', content: <ParentRequestsPendingPanel /> });
  // Bilgi guncelleme istekleri AYRI panel: yalnizca yonetici gorur, ogretmen
  // panosunda hic cizilmez (bilesen kendi icinde de kontrol ediyor).
  widgets.push({ id: 'parent-profile-change', content: <ParentProfileChangePendingPanel /> });
  widgets.push({ id: 'leave-requests', content: <LeaveRequestsPendingPanel /> });

  widgets.push({
    id: 'missing-projects',
    content: (
      <MissingProjectsPanel
        todaySessions={data?.missing_projects_today || []}
        loading={loadingMain}
      />
    ),
  });

  if (loadingMain || announcements.length > 0) {
    widgets.push({
      id: 'announcements',
      content: loadingMain ? (
        <Card size="small"><Skeleton active paragraph={{ rows: 2 }} /></Card>
      ) : (
        <Card title="Son Duyurular" size="small">
          <Collapse ghost size="small" items={announcements.map(a => ({
            key: a.id,
            label: (
              <div className="flex justify-between items-center w-full pr-4">
                <span className="font-semibold text-sm">{a.title}</span>
                <Text type="secondary" className="text-xs shrink-0 ml-2">{dayjs(a.published_at).format('DD.MM.YYYY')}</Text>
              </div>
            ),
            children: (
              <div style={{ padding: '4px 8px' }}>
                {a.image_url && <Image src={a.image_url} alt="" style={{ maxHeight: 180, borderRadius: 8, marginBottom: 8, display: 'block', cursor: 'pointer' }} />}
                <div className="text-gray-600 text-sm ql-editor prose max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.body) }} />
              </div>
            ),
          }))} />
        </Card>
      ),
    });
  }

  widgets.push({
    id: 'notes',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={<span><FileTextOutlined className="mr-2 text-orange-400" />Günlük Notlar</span>}
            size="small"
            extra={<a className="text-xs" onClick={() => navigate('/daily-notes')}>Tümü →</a>}
          >
            {loadingNotes ? <SectionSpin /> : notes.length === 0 ? (
              <Empty description="Bugün not yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List size="small" dataSource={notes} renderItem={n => (
                <List.Item>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      {n.branch && <Tag color="geekblue" style={{ margin: 0 }}>{n.branch.name}</Tag>}
                      <Text type="secondary" className="text-xs">
                        {n.user?.name} {n.user?.surname} · {dayjs(n.created_at).format('HH:mm')}
                      </Text>
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{n.content}</div>
                  </div>
                </List.Item>
              )} />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <CollapsibleCard
            title={<span><FileTextOutlined className="mr-2 text-blue-500" />Bugünün Ders Notları</span>}
          >
            {loadingSchedule ? <SectionSpin /> : lessonNotes.length === 0 ? (
              <Empty description="Bugün sertifika programı notu eklenmemiş" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List size="small" dataSource={lessonNotes} renderItem={s => (
                <List.Item>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <Tag style={{ margin: 0, background: s.color ? `${s.color}22` : '#e6f4ff', color: s.color || '#1677ff', borderColor: s.color || '#91caff' }}>
                        {s.classroom_name}
                      </Tag>
                      <Text type="secondary" className="text-xs">{s.start_time?.slice(0, 5)}</Text>
                      {s.teachers?.length > 0 && (
                        <Text type="secondary" className="text-xs">· {s.teachers.map(t => t.name).join(', ')}</Text>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{s.note}</div>
                  </div>
                </List.Item>
              )} />
            )}
          </CollapsibleCard>
        </Col>
      </Row>
    ),
  });

  // KOŞULSUZ — gerekçe AdminDashboard'daki aynı bloğun başında.
  const ogretmensizDersler = data?.unassigned_sessions || [];
  widgets.push({
      id: 'unassigned-sessions',
      content: loadingMain ? (
        <Card size="small"><Skeleton active paragraph={{ rows: 2 }} /></Card>
      ) : (
        <Card
          size="small"
          title={
            <span className={ogretmensizDersler.length > 0 ? 'text-orange-500' : undefined}>
              <WarningOutlined className="mr-2" style={{ color: ogretmensizDersler.length > 0 ? undefined : '#bfbfbf' }} />
              Öğretmensiz Sertifika Programı Dersleri — Önümüzdeki 7 Gün
              <Badge
                count={ogretmensizDersler.length}
                showZero
                style={{ backgroundColor: ogretmensizDersler.length > 0 ? '#faad14' : '#d9d9d9', marginLeft: 8 }}
              />
            </span>
          }
          style={ogretmensizDersler.length > 0 ? { borderColor: '#fa8c16' } : undefined}
        >
          {ogretmensizDersler.length === 0 ? (
            <Empty
              description="Önümüzdeki 7 günde öğretmeni atanmamış ders yok."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : ogretmensizDersler.map(s => {
            const st = assignState[s.id];
            return (
              <div key={s.id} className="py-2 border-b last:border-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{s.classroom?.name}</span>
                    {s.classroom?.branch?.name && <Tag color="geekblue">{s.classroom.branch.name}</Tag>}
                    <Text type="secondary" className="text-xs">
                      {dayjs(s.date).format('DD.MM.YYYY')} · {s.start_time?.slice(0, 5)}
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="small" type={st?.open ? 'default' : 'primary'} icon={<UserOutlined />} onClick={() => toggleAssign(s.id)}>
                      {st?.open ? 'İptal' : 'Öğretmen Ata'}
                    </Button>
                  </div>
                </div>
                {st?.open && (
                  <div className="flex items-center gap-2 mt-2">
                    <UserSearchSelect
                      mode="multiple" size="small" style={{ flex: 1 }}
                      placeholder="Öğretmen ara..."
                      value={st.selected}
                      onChange={val => setAssignState(prev => ({ ...prev, [s.id]: { ...prev[s.id], selected: val } }))}
                      extraParams={{ role: ['teacher', 'branch_manager', 'super_admin'] }}
                      renderLabel={(t) => t.branch?.name ? `${t.name} ${t.surname} (${t.branch.name})` : `${t.name} ${t.surname}`}
                    />
                    <Button size="small" type="primary" loading={st.saving} onClick={() => handleAssign(s)}>Kaydet</Button>
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      ),
  });

  if (loadingAbsences || absences.length > 0) {
    widgets.push({
      id: 'today-absences',
      content: loadingAbsences ? (
        <Card size="small"><Skeleton active paragraph={{ rows: 1 }} /></Card>
      ) : (
        <Card
          size="small"
          title={<span className="text-red-500"><WarningOutlined className="mr-2" />Bugünün Devamsızları — Mazeretli Yap ({absences.length})</span>}
          style={{ borderColor: '#ff4d4f' }}
        >
          {absences.map(a => (
            <div key={a.attendance_id} className="flex justify-between items-center py-2 border-b last:border-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{a.student_name}</span>
                <Tag style={{ margin: 0 }}>{a.classroom_name}</Tag>
                <Text type="secondary" className="text-xs">{a.start_time?.slice(0, 5)}</Text>
              </div>
            </div>
          ))}
        </Card>
      ),
    });
  }

  widgets.push({
    id: 'kpi',
    content: (
      <Row gutter={[16, 16]}>
        {loadingMain ? (
          [1,2,3,4,5,6].map(i => (
            <Col key={i} xs={12} sm={8} lg={4}>
              <Card size="small"><Skeleton active paragraph={{ rows: 1 }} title={false} /></Card>
            </Col>
          ))
        ) : [{
          title: 'Bugünkü Sertifika Programı Dersleri', value: data?.today?.sessions,          icon: <CalendarOutlined />, color: '#1677ff', path: '/calendar' },
          { title: 'Bugün Gelen',     value: data?.today?.present,            icon: <TeamOutlined />,     color: '#52c41a' },
          { title: 'Aktif Öğrenci',   value: data?.students?.active,          icon: <TeamOutlined />,     color: '#1677ff', path: '/students' },
          { title: 'Bekleyen Aday',   value: data?.students?.candidate,       icon: <UserAddOutlined />,  color: '#faad14', path: '/students?status=candidate' },
          // Para kartlarinda isMoney: AntD'nin varsayilan bicimlendiricisi
          // Amerikan ayracli ve ondalik sayisini gelen degerden aliyor -
          // biri "28,400.00 ₺" digeri "836,125 ₺" cikiyordu.
          { title: 'Bu Ay Tahsilat',  value: data?.finance?.monthly_payments, icon: <DollarOutlined />,   color: '#52c41a', isMoney: true, path: '/payments' },
          { title: 'Toplam Alacak',   value: data?.finance?.total_debt,       icon: <DollarOutlined />,   color: '#ff4d4f', isMoney: true },
        ].map((kpi, i) => (
          <Col key={i} xs={12} sm={8} lg={4}>
            <Card
              size="small"
              className={kpi.path ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
              onClick={kpi.path ? () => navigate(kpi.path) : undefined}
            >
              <Statistic title={kpi.title} value={kpi.value ?? 0} prefix={kpi.icon} suffix={kpi.suffix}
                formatter={kpi.isMoney ? (v) => formatMoney(v) : undefined}
                valueStyle={{ color: kpi.color, fontSize: 20 }} />
            </Card>
          </Col>
        ))}
      </Row>
    ),
  });

  widgets.push({
    id: 'recent',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={<span><UserSwitchOutlined className="mr-2 text-green-500" />Son 7 Günde Yeni Kayıtlar ({data?.recent_students?.length ?? 0})</span>}
            size="small"
            extra={<a className="text-xs" onClick={() => navigate('/students')}>Tümü →</a>}
          >
            {!data?.recent_students?.length ? (
              <Empty description="Bu hafta yeni kayıt yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              data.recent_students.map(s => (
                <div key={s.id} className="flex justify-between items-center py-1.5 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{s.name}</span>
                    {s.branch && <Tag color="geekblue" style={{ margin: 0 }}>{s.branch}</Tag>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag color={s.status === 'active' ? 'green' : 'orange'} style={{ margin: 0 }}>
                      {s.status === 'active' ? 'Aktif' : 'Aday'}
                    </Tag>
                    <Text type="secondary" className="text-xs">{dayjs(s.created_at).format('DD.MM')}</Text>
                  </div>
                </div>
              ))
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={<span><DollarOutlined className="mr-2 text-red-500" />En Yüksek Borçlular</span>}
            size="small"
            extra={<a className="text-xs" onClick={() => navigate('/payments')}>Tümü →</a>}
          >
            {!data?.top_debtors?.length ? (
              <Empty description="Borçlu öğrenci yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              data.top_debtors.map(s => {
                const daysSince = s.oldest_unpaid_at ? dayjs().diff(dayjs(s.oldest_unpaid_at), 'day') : null;
                return (
                  <div key={s.id} className="flex justify-between items-center py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{s.name}</span>
                      {s.branch && <Tag color="geekblue" style={{ margin: 0 }}>{s.branch}</Tag>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-red-500 text-sm">{formatMoney(s.total_debt)}</span>
                      {daysSince !== null && (
                        <Tooltip title={`İlk ödenmemiş: ${dayjs(s.oldest_unpaid_at).format('DD.MM.YYYY')}`}>
                          <Tag color={daysSince > 30 ? 'red' : 'orange'} style={{ margin: 0 }}>{daysSince} gün</Tag>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </Card>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'trends-1',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Aylık Gelir Trendi (12 Ay)" size="small">
            {loadingTrend ? <SectionSpin /> : <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trendFormatted}>
                <defs>
                  <linearGradient id="revGradM" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#1677ff" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#1677ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <ChartTooltip formatter={v => [formatMoney(v), 'Gelir']} />
                <Area type="monotone" dataKey="revenue" stroke="#1677ff" strokeWidth={2} fill="url(#revGradM)" />
              </AreaChart>
            </ResponsiveContainer>}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Öğrenci Durumu" size="small">
            {loadingMain ? <SectionSpin /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={studentPie} cx="50%" cy="45%" innerRadius={55} outerRadius={80} dataKey="value" paddingAngle={3}>
                    {studentPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <ChartTooltip formatter={(v, name) => [v, name]} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'trends-2',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Yeni Öğrenci Trendi (12 Ay)" size="small">
            {loadingTrend ? <SectionSpin /> : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={trendFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ChartTooltip />
                  <Bar dataKey="new_students"    name="Aday Öğrenci" fill="#1677ff" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="new_enrollments" name="Yeni Öğrenci" fill="#91caff" radius={[3, 3, 0, 0]} />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={6}>
          <Card title="Bugün Devam" size="small">
            {loadingMain ? <SectionSpin /> : (attendancePie[0].value === 0 && attendancePie[1].value === 0) ? (
              <Empty description="Henüz yoklama alınmadı" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={attendancePie} cx="50%" cy="45%" innerRadius={48} outerRadius={72} dataKey="value" paddingAngle={3}>
                    <Cell fill="#52c41a" />
                    <Cell fill="#ff4d4f" />
                  </Pie>
                  <ChartTooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={6}>
          <Card title="Bugün Derslik Doluluk" size="small" styles={{ body: { maxHeight: 200, overflowY: 'auto' } }}>
            {loadingSchedule ? <SectionSpin /> : classroomOccupancy.length === 0
              ? <Empty description="Bugün sertifika programı dersi yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              : classroomOccupancy.map((c, i) => (
                <div key={i} className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="truncate font-medium">{c.name}</span>
                    <span className="shrink-0 ml-1 text-gray-500">{c.total}/{c.capacity_sum} · %{c.rate}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full" style={{
                      width: `${Math.min(c.rate, 100)}%`,
                      background: c.rate >= 90 ? '#ff4d4f' : c.rate >= 70 ? '#faad14' : '#1677ff',
                    }} />
                  </div>
                </div>
              ))
            }
          </Card>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'missing-attendances',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <MissingAttendancesPanel />
        </Col>
      </Row>
    ),
  });

  return <DraggableDashboard storageKey="branch-manager" widgets={widgets} />;
};

export default BranchManagerDashboard;
